import { useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@/shared/ui/theme/ThemeProvider';
import { useProfile } from '@/modules/profiles/presentation/context/ProfileContext';
import { resolveStructureCode } from '@/modules/profiles/domain/entities/AccessRole';
import { CameraFeed } from '@/presentation/components/CameraFeed';
import { StepRow } from '@/presentation/components/StepRow';
import { AuthMethodSwitcher } from '@/presentation/components/AuthMethodSwitcher';
import type { AttendanceAction } from '../../domain/entities/Timelog';
import { unlockAdminSession } from '@/modules/admin/application/adminSession';
import { useScanLifecycle } from '../hooks/useScanLifecycle';
import { useVisualScanPhase } from '../hooks/useVisualScanPhase';
import { useRegisterTimelog } from '../hooks/useRegisterTimelog';
import { DataUriPhoto } from '../../domain/valueObjects/DataUriPhoto';
import { CLOCK_WEB_MODE, TIMELOG_SOURCE } from '../../domain/constants/verificationMode';
import { useFaceEmbeddingsCountQuery } from '@/modules/attendance/application/faceQueries';
import { useGeolocation } from '@/shared/hooks/useGeolocation';
import { useResolvedAddress } from '@/shared/hooks/useResolvedAddress';
import { useQualitySettings } from '@/shared/hooks/useQualitySettings';
// OLD: getTimelogRepository no longer needed (automatic mode now uses accessType = 0)
// import { getTimelogRepository } from '../../infrastructure/repositories/container';
import { OnlineTimelogError } from '../../infrastructure/repositories/CompositeTimelogRepository';
import { isDefinitelyOnline } from '@/shared/onlineState';
import { enrichEmployeePhotoUrl } from '../../infrastructure/helpers/enrichEmployeePhotoUrl';
import { EmbeddingsUnavailableTooltip } from '../components/EmbeddingsUnavailableTooltip';

function actionColor(action: AttendanceAction, C: { success: string; danger: string; blue: string; primary: string }): string {
  if (action === 'entrada')      return C.blue; // was C.success — unified to C.blue (Slice B)
  if (action === 'salida')       return C.blue; // was C.danger  — unified to C.blue (Slice B)
  if (action === 'automatic')    return C.blue; // system decides — same unified blue
  if (action === 'acceso-admin') return C.blue;
  return C.blue; // acceso-empleado
}

export function ScanningPage() {
  const { t } = useTranslation('common');
  const { C, T } = useTheme();
  const navigate = useNavigate();
  const { action = 'entrada' } = useParams<{ action: string }>();
  const { selectedRole } = useProfile();
  const typedAction = action as AttendanceAction;
  const accent = actionColor(typedAction, C);

  // Quality settings — loaded from db.settings; gate scan until ready
  const {
    thresholds,
    livenessMode,
    qualityGateTimeoutMs,
    matchThreshold,
    consecutiveTimelogMinSeconds,
    isLoaded: qualitySettingsLoaded,
  } = useQualitySettings();

  // ScanningPage IS the facial recognition flow exclusively. Pre-fetch embeddings
  // for role-scoped attendance actions (entrada / salida). Admin / employee access
  // match against the global pool, so no role-scoped pre-fetch is needed.
  // NOTE: we intentionally do NOT inspect `selectedRole.verificationModes` here —
  // doing so would break roles like ['pin','face'] (same anti-pattern that caused
  // the runtime bug where vMode=13 was sent instead of vMode=11).
  const isFaceMode = typedAction !== 'acceso-admin' && typedAction !== 'acceso-empleado';

  const {
    data:                embeddingsCount,
    isLoading:           isLoadingEmbeddings,
    noFacialEnrollments,
  } = useFaceEmbeddingsCountQuery(isFaceMode ? (selectedRole?.idAccessRole ?? null) : null);

  // If not face-mode, embeddings are irrelevant — treat as ready immediately
  const embeddingsReady = !isFaceMode || (!isLoadingEmbeddings && (embeddingsCount ?? 0) > 0);

  const enableQualityGate = import.meta.env.VITE_ENABLE_QUALITY_GATE !== 'false';

  const {
    phase,
    videoRef,
    cameraActive,
    matchedEmployee,
    error,
    qualityHint,
    livenessDirection,
  } = useScanLifecycle({
    action: typedAction,
    accessRoleId: selectedRole?.idAccessRole ?? null,
    enabled: embeddingsReady && qualitySettingsLoaded && !noFacialEnrollments,
    enableQualityGate,
    thresholds,
    livenessMode,
    qualityGateTimeoutMs,
    matchThreshold,
  });

  const visualPhase = useVisualScanPhase(phase);

  const { coordinates } = useGeolocation();
  const { address } = useResolvedAddress(coordinates);
  const registerTimelog = useRegisterTimelog();

  // submittedRef guards single submission per recognition event
  const submittedRef = useRef<boolean>(false);

  // Reset submittedRef when phase returns to scanning/idle (new scan cycle)
  useEffect(() => {
    if (phase === 'scanning' || phase === 'idle') {
      submittedRef.current = false;
    }
  }, [phase]);

  // On match → navigate based on action type
  useEffect(() => {
    if (phase !== 'matched' || !matchedEmployee) return;

    // Acceso Empleado: no timelog, go directly to employee portal
    if (typedAction === 'acceso-empleado') {
      // Enrich matchedEmployee with photoUrl from cached EmployeeDTO before navigate
      void enrichEmployeePhotoUrl(matchedEmployee).then((enriched) => {
        void navigate(`/empleado/${matchedEmployee.idPerson}`, {
          state: { employee: enriched },
          replace: true,
        });
      });
      return;
    }

    // Acceso Admin: unlock admin session, no timelog
    if (typedAction === 'acceso-admin') {
      unlockAdminSession();
      void navigate('/admin', { replace: true });
      return;
    }

    // Entrada / Salida / Automatic: create timelog with matched employee
    // submittedRef prevents double-submit on re-render
    if (submittedRef.current) return;
    if (!selectedRole) return;

    submittedRef.current = true;

    const rawPhoto = matchedEmployee.lastFramePhoto.value;
    let photo: DataUriPhoto | undefined;
    try {
      photo = rawPhoto ? DataUriPhoto.from(rawPhoto) : undefined;
    } catch {
      photo = undefined;
    }

    void (async () => {
      // NEW: Use accessType = 0 for automatic mode (backend decides entrada/salida)
      // OLD: Client-side logic looked up last timelog and toggled 1→2 or 2→1
      let accessType: 0 | 1 | 2 = typedAction === 'salida' ? 2 : typedAction === 'entrada' ? 1 : 0;
      let resolvedAction: AttendanceAction = 
        accessType === 2 ? 'salida' : 
        accessType === 1 ? 'entrada' : 
        'automatic';  // accessType 0 → backend decides
      
      // OLD automatic logic (commented for rollback):
      // if (typedAction === 'automatic') {
      //   try {
      //     const repo = getTimelogRepository();
      //     const last = await repo.getLastByPerson(matchedEmployee.idPerson);
      //     accessType = last?.accessType === 1 ? 2 : 1;
      //   } catch (err) {
      //     console.warn('[ScanningPage] Automatic lookup failed — defaulting to entrada:', err);
      //     accessType = 1;
      //   }
      // }
      // const resolvedAction: AttendanceAction = accessType === 2 ? 'salida' : 'entrada';

      const idStructure = selectedRole.structures[0] ?? 0;

      try {
        await registerTimelog.execute({
          employee: {
            idEntity: matchedEmployee.idEntity,
            idBiometric: matchedEmployee.matchedBiometricId,
          },
          idStructure,
          structureCode: resolveStructureCode(selectedRole, idStructure),
          accessType,
          verificationMode: CLOCK_WEB_MODE.facial,
          ...(photo !== undefined ? { photo } : {}),
          isOffline: !isDefinitelyOnline(),
          coordinates: coordinates ?? { lat: 0, lng: 0 },
          location: address,
          source: TIMELOG_SOURCE.clockWeb,
          consecutiveMinSeconds: consecutiveTimelogMinSeconds,
        });
        const enriched = await enrichEmployeePhotoUrl(matchedEmployee);
        void navigate('/success', {
          state: {
            action: resolvedAction,
            employee: { ...enriched, pinCode: null },
          },
          replace: true,
        });
      } catch (err) {
        // Online/offline hard failure. The composite repo throws
        // OnlineTimelogError for backend rejections (online) or consecutive
        // guard violations (offline). `code` + `elapsedSeconds` let
        // MarkingErrorPage pick specific copy.
        const errorCode = err instanceof OnlineTimelogError ? err.code : undefined;
        const elapsedSeconds = err instanceof OnlineTimelogError ? err.elapsedSeconds : undefined;
        const minSeconds = err instanceof OnlineTimelogError ? err.minSeconds : undefined;
        void navigate('/error', {
          state: { action: resolvedAction, method: 'facial', errorCode, elapsedSeconds, minSeconds },
          replace: true,
        });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, matchedEmployee]);

  // On auth failures → navigate to dedicated face-auth error page.
  // Auth failures: face present but authentication/quality could not succeed.
  //   NO_MATCH:            face recognized but no embedding matched
  //   QUALITY_TIMEOUT:     quality gate ran for 5s without passing (bad lighting/position)
  //   LOW_QUALITY:         inferEmbedding rejected the photo quality
  //   SPOOF:               legacy MiniFASNetV2 single-frame fraud detection code (kept for BC)
  //   NO_FACE:             Slice B — no face detected in the scan loop (previously swallowed)
  //   MULTIPLE_FACES:      Slice B — multiple faces detected (previously swallowed)
  //   LIVENESS_BORDERLINE: Slice B — legacy 3-band borderline verdict (previously swallowed)
  //   LIVENESS_SPOOF:      Slice B — LivenessAggregator terminal spoof verdict
  //   LIVENESS_INCONCLUSIVE: Slice B — LivenessAggregator ran out of frames without a decision
  // Infrastructure errors (MODEL_LOAD, CAMERA_DENIED, CAMERA_ERROR) stay inline —
  // they fire before any recognition attempt, often in milliseconds.
  useEffect(() => {
    if (phase !== 'error' && phase !== 'denied') return;
    const code = error?.code;
    const isAuthFailure =
      code === 'NO_MATCH' ||
      code === 'QUALITY_TIMEOUT' ||
      code === 'LOW_QUALITY' ||
      code === 'SPOOF' ||
      code === 'NO_FACE' ||
      code === 'MULTIPLE_FACES' ||
      code === 'LIVENESS_BORDERLINE' ||
      code === 'LIVENESS_SPOOF' ||
      code === 'LIVENESS_INCONCLUSIVE';
    if (!isAuthFailure) return; // keep infrastructure errors inline
    // LOW_QUALITY carries a subreason ('blur' | 'brightness' | 'size') in
    // error.message, set by useScanLifecycle from LowQualityError.reason.
    // Forward it to FaceAuthErrorPage so the operator sees the specific cause
    // instead of the generic faceError.lowQuality fallback.
    const subreason =
      code === 'LOW_QUALITY' && (error?.message === 'blur' || error?.message === 'brightness' || error?.message === 'size')
        ? error.message
        : undefined;
    void navigate('/face-error', {
      state: {
        action: typedAction,
        errorCode: code,
        ...(subreason !== undefined && { subreason }),
      },
      replace: true,
    });
  }, [phase, error, typedAction, navigate]);

  // Phase → StepRow state (uses visualPhase to suppress comparing↔scanning flicker)
  const stepCaptureDone = ['camera-ready', 'scanning', 'comparing', 'matched'].includes(visualPhase);
  const stepFacialLoading = visualPhase === 'scanning';
  const stepFacialDone = ['comparing', 'matched'].includes(visualPhase);
  const stepIdentityLoading = visualPhase === 'comparing';
  const stepIdentityDone = visualPhase === 'matched';
  const stepServerLoading = registerTimelog.isPending;
  const stepServerDone = registerTimelog.isSuccess;

  const statusKey =
    visualPhase === 'loading-models' ? 'scan.loadingModels' :
      visualPhase === 'camera-prompt' ? 'scan.cameraPrompt' :
        visualPhase === 'denied' ? 'scan.cameraDenied' :
          visualPhase === 'comparing' ? 'scan.processing' :
            // QUALITY_TIMEOUT, NO_MATCH, LOW_QUALITY, SPOOF → navigate to /face-error (no inline statusKey needed)
            visualPhase === 'error' && error?.code === 'NO_FACE' ? 'scan.noFaceDetected' :
              visualPhase === 'error' && error?.code === 'MODEL_LOAD' ? 'scan.modelsUnavailable' :
                error?.code === 'LOW_QUALITY' ? 'scan.lowQuality' :
                  'scan.analyzing';

  const showSpinner = ['loading-models', 'camera-prompt', 'quality-checking', 'scanning', 'comparing'].includes(visualPhase);

  const isAdmin = typedAction === 'acceso-admin';
  const isEmployee = typedAction === 'acceso-empleado';
  const isAttendance = !isAdmin && !isEmployee;

  /** Maps liveness direction to its i18n key */
  const LIVENESS_HINT_KEY: Record<string, string> = {
    right: 'scan.hint.livenessRight',
    left: 'scan.hint.livenessLeft',
    up: 'scan.hint.livenessUp',
    down: 'scan.hint.livenessDown',
  };

  /** The hint text to display: liveness direction takes priority over quality hint */
  const activeHintKey: string | null =
    livenessDirection !== null
      ? (LIVENESS_HINT_KEY[livenessDirection] ?? null)
      : qualityHint;

  return (
    <main style={{ flex: 1, background: T.bg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '30px 30px 26px', overflow: 'hidden', position: 'relative' }}>
      {/* Quality settings not loaded yet — wait for db.settings before starting scan */}
      {!qualitySettingsLoaded && (
        <div
          data-testid="quality-settings-loading"
          style={{
            position: 'absolute',
            top: 0, left: 0, right: 0, bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            background: T.bg,
            gap: 12,
            zIndex: 10,
          }}
        >
          <div className="mi-anim-spin" style={{ width: 24, height: 24, border: `3px solid ${T.border}`, borderTopColor: C.primary, borderRadius: '50%' }} />
          <span style={{ fontSize: 13, fontWeight: 600, color: T.textMuted }}>
            {t('scan.loadingSettings')}
          </span>
        </div>
      )}
      {/* Embeddings not ready yet — show sync hint instead of starting scan loop.
          Suppressed when noFacialEnrollments (cap reached with zero seeds) so the
          operator sees the EmbeddingsUnavailableTooltip instead of an endless spinner. */}
      {!embeddingsReady && !isLoadingEmbeddings && !noFacialEnrollments && (
        <div style={{
          position: 'absolute',
          top: 0, left: 0, right: 0, bottom: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: T.bg,
          gap: 12,
          zIndex: 10,
        }}>
          <div className="mi-anim-spin" style={{ width: 24, height: 24, border: `3px solid ${T.border}`, borderTopColor: C.primary, borderRadius: '50%' }} />
          <span style={{ fontSize: 13, fontWeight: 600, color: T.textMuted }}>
            {t('scan.syncingEmbeddings')}
          </span>
        </div>
      )}
      <div className="mi-anim-fade-in" style={{ flex: 1, width: '100%', maxWidth: 540, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0 }}>
        {/* Header */}
        <div style={{ width: '100%', position: 'relative', display: 'flex', alignItems: 'center', marginBottom: 24 }}>
          <button
            onClick={() => { void navigate('/'); }}
            style={{ background: 'transparent', border: `1px solid ${T.border}`, borderRadius: 8, padding: '7px 12px', cursor: 'pointer', color: T.text, fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 600, zIndex: 1 }}
          >
            <svg width={11} height={11} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            {t('scan.back')}
          </button>
          <div style={{ position: 'absolute', left: 0, right: 0, textAlign: 'center', fontSize: 14, fontWeight: 700, color: T.text, pointerEvents: 'none' }}>
            {typedAction === 'acceso-empleado' ? t('scan.titleIdentity') : typedAction === 'acceso-admin' ? t('scan.titleAdmin') : t('scan.titleFacial')}
          </div>
          <div style={{ marginLeft: 'auto', padding: '5px 15px', borderRadius: 15, fontSize: 9, fontWeight: 700, letterSpacing: '.06em', zIndex: 1, background: `${accent}18`, color: accent }}>
            {typedAction === 'entrada' ? t('scan.labelEntry') : typedAction === 'salida' ? t('scan.labelExit') : typedAction === 'acceso-admin' ? t('scan.labelAdmin') : t('scan.labelAccess')}
          </div>
        </div>

        {/* Camera */}
        <div style={{ position: 'relative', marginTop: 48, marginBottom: 65 }}>
          <CameraFeed
            scanning={phase === 'scanning' || phase === 'comparing'}
            size={195}
            T={T}
            videoRef={videoRef}
            cameraActive={cameraActive}
            showSpinner={phase === 'camera-prompt'}
          />
          {/* Quality hint overlay — non-blocking, shown during quality-checking phase */}
          {activeHintKey !== null && (
            <div
              data-testid="quality-hint-overlay"
              style={{
                position: 'absolute',
                bottom: -55,
                left: '50%',
                transform: 'translateX(-50%)',
                background: `${C.primary}18`,
                border: `1px solid ${C.primary}40`,
                borderRadius: 20,
                padding: '5px 14px',
                fontSize: 10,
                fontWeight: 700,
                color: C.primary,
                whiteSpace: 'nowrap',
                letterSpacing: '.03em',
                pointerEvents: 'none',
              }}
            >
              {t(activeHintKey)}
            </div>
          )}
        </div>

        {/* Status */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, marginTop: 10 }}>
          {showSpinner && (
            <div className="mi-anim-spin" style={{ width: 16, height: 16, border: `2px solid ${T.textMuted}`, borderTopColor: C.primary, borderRadius: '50%' }} />
          )}
          <span style={{ fontSize: 11, fontWeight: 600, color: T.text }}>
            {t(statusKey)}
          </span>
        </div>

        {/* Steps */}
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 42 }}>
          <StepRow label={t('scan.stepCapture')} done={stepCaptureDone} T={T} />
          <StepRow label={t('scan.stepFacial')} done={stepFacialDone} loading={stepFacialLoading} T={T} />
          <StepRow label={t('scan.stepIdentity')} done={stepIdentityDone} loading={stepIdentityLoading} T={T} />
          <StepRow label={t('scan.stepServer')} done={stepServerDone} loading={stepServerLoading} T={T} />
        </div>

        {/* Empty-embeddings escape hatch — non-blocking tooltip. AuthMethodSwitcher below
            remains reachable so the operator can switch to PIN or Card. */}
        {noFacialEnrollments && <EmbeddingsUnavailableTooltip />}

        {/* Fallback buttons — only shown after facial recognition fails */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {isAdmin && true && (
            <button
              onClick={() => { void navigate('/id-input/acceso-admin'); }}
              style={{ padding: '8px 18px', borderRadius: 8, border: `1px solid ${C.blue}40`, background: `${C.blue}10`, color: C.blue, fontFamily: 'inherit', fontSize: 10, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <svg width={11} height={11} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
              {t('methods.usePin')}
            </button>
          )}
          {isAdmin && true && (
            <button
              onClick={() => { void navigate('/card-scan/acceso-admin'); }}
              style={{ padding: '8px 18px', borderRadius: 8, border: `1px solid ${C.blue}40`, background: `${C.blue}10`, color: C.blue, fontFamily: 'inherit', fontSize: 10, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <svg width={11} height={11} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="6" width="20" height="13" rx="2" /><path d="M16 12h.01M13 12h.01" /></svg>
              {t('methods.useCard')}
            </button>
          )}
          {isEmployee && true && (
            <button
              onClick={() => { void navigate('/id-input/acceso-empleado'); }}
              style={{ padding: '8px 18px', borderRadius: 8, border: `1px solid ${accent}40`, background: `${accent}10`, color: accent, fontFamily: 'inherit', fontSize: 10, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <svg width={11} height={11} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
              {t('methods.usePin')}
            </button>
          )}
          {isEmployee && true && (
            <button
              onClick={() => { void navigate('/card-scan/acceso-empleado'); }}
              style={{ padding: '8px 18px', borderRadius: 8, border: `1px solid ${accent}40`, background: `${accent}10`, color: accent, fontFamily: 'inherit', fontSize: 10, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <svg width={11} height={11} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="6" width="20" height="13" rx="2" /><path d="M16 12h.01M13 12h.01" /></svg>
              {t('methods.useCard')}
            </button>
          )}
          {isAttendance && (
            <AuthMethodSwitcher
              currentMethod="facial"
              action={typedAction}
              accent={accent}
              t={t}
            />
          )}
          <button
            onClick={() => { void navigate('/'); }}
            style={{ padding: '8px 22px', borderRadius: 8, border: 'none', background: C.danger, color: '#fff', fontFamily: 'inherit', fontSize: 10, fontWeight: 700, cursor: 'pointer' }}
          >
            {t('scan.cancel')}
          </button>
        </div>
      </div>
    </main>
  );
}
